import React, { useCallback, useEffect, useState } from 'react'
import { Avatar, Badge, Dropdown, Popover, Skeleton, Switch } from 'antd'
import { Icon } from '@iconify/react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'

import { showActionNotification } from '../../components/notification'
import { loginState } from '../../modules/login/login.slice'
import {
    fetchSupportNotifications,
    markAllSupportNotificationsRead,
    markSupportNotificationRead,
    SUPPORT_NOTIFICATIONS_PAGE_LIMIT,
    supportNotificationsState,
} from '../../modules/notifications/notifications.slice'
import { PRIVATE_ROUTES } from '../../routes'
import styles from './HeaderNotifications.module.css'

const NOTIFICATIONS_SCROLL_ID = 'header-notifications-scroll'

const NOTIFICATION_TYPE_LABEL = {
    supportTasks: 'Support',
    task: 'Work Navigator',
    development: 'Work Navigator',
}

const formatTypeLabel = (value) =>
    String(value || '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')

const getTypeBadgeClassName = (type) => {
    switch (type) {
        case 'supportTasks':
            return styles.rowMetaBadgeSupport
        case 'task':
        case 'development':
            return styles.rowMetaBadgeWork
        default:
            return styles.rowMetaBadgeDefault
    }
}

const formatRelativeTime = (iso) => {
    if (!iso) return ''
    const t = new Date(iso).getTime()
    if (Number.isNaN(t)) return ''
    const sec = Math.floor((Date.now() - t) / 1000)
    if (sec < 60) return 'just now'
    if (sec < 3600) {
        const m = Math.floor(sec / 60)
        return `${m} min ago`
    }
    if (sec < 86400) {
        const h = Math.floor(sec / 3600)
        return `${h} hour${h === 1 ? '' : 's'} ago`
    }
    if (sec < 604800) {
        const d = Math.floor(sec / 86400)
        return `${d} day${d === 1 ? '' : 's'} ago`
    }
    const w = Math.floor(sec / 604800)
    return `${w} week${w === 1 ? '' : 's'} ago`
}

const getInitials = (name) => {
    if (!name) return '?'
    const parts = String(name).trim().split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const getActorAvatarColor = (name) => {
    if (!name) return '#e2e8f0'
    const colors = [
        '#85BBF9',
        '#6366f1',
        '#8b5cf6',
        '#ec4899',
        '#f59e0b',
        '#10b981',
        '#06b6d4',
    ]
    const index = name.charCodeAt(0) % colors.length
    return colors[index]
}

const getActorDisplay = (item) => {
    const data = item?.notificationData || {}
    const profilePicture =
        item?.actor?.profilePicture ||
        item?.recipient?.profilePicture ||
        null
    const safePicture =
        profilePicture && !String(profilePicture).startsWith('file://')
            ? profilePicture
            : null
    return {
        name: item?.actor?.name || data?.notifyBy || '',
        profilePicture: safePicture,
    }
}

const pickQuotedTitle = (body) => {
    const m = String(body || '').match(/"([^"]+)"/)
    return m ? m[1] : null
}

const pickSupportTaskProjectAndTaskIds = (item) => {
    if (item?.projectId && item?.taskId) {
        return {
            projectId: String(item.projectId),
            taskId: String(item.taskId),
        }
    }

    const routes = Array.isArray(item?.routeId) ? item.routeId : []
    const data = item?.notificationData || {}

    if (routes.length >= 2) {
        return {
            projectId: String(routes[0]),
            taskId: String(routes[1]),
        }
    }

    if (routes.length === 1) {
        const taskId = String(routes[0])
        const projectId =
            data.projectId ||
            data?.project?._id ||
            item?.projectId ||
            (typeof data?.project === 'string' ? data.project : null)
        if (projectId) {
            return { projectId: String(projectId), taskId }
        }
    }

    const projectId =
        data.projectId ||
        data?.project?._id ||
        item?.projectId ||
        (typeof data?.project === 'string' ? data.project : null)
    const taskId = data.taskId || data?.task?._id || null

    if (projectId && taskId) {
        return { projectId: String(projectId), taskId: String(taskId) }
    }

    return { projectId: null, taskId: null }
}

const buildNotificationFetchArgs = ({
    skip = 0,
    reset = true,
    unreadOnly = false,
    limit = SUPPORT_NOTIFICATIONS_PAGE_LIMIT,
}) => ({
    skip,
    limit,
    reset,
    ...(unreadOnly ? { unRead: true } : {}),
})

const NotificationsPanel = ({ onClose, unreadOnly, onUnreadOnlyChange }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { list, status, hasNext, nextSkip, error, pageLimit } = useSelector(
        supportNotificationsState
    )

    const isLoading = status === 'loading'
    const isLoadingMore = status === 'loadingMore'
    const canLoadMore = hasNext && !isLoading && !isLoadingMore

    const handleUnreadOnlyChange = (checked) => {
        onUnreadOnlyChange?.(checked)
        dispatch(
            fetchSupportNotifications(
                buildNotificationFetchArgs({
                    skip: 0,
                    reset: true,
                    unreadOnly: checked,
                    limit: pageLimit,
                })
            )
        )
    }

    const loadMoreNotifications = useCallback(() => {
        if (!canLoadMore) return
        dispatch(
            fetchSupportNotifications(
                buildNotificationFetchArgs({
                    skip: nextSkip ?? list.length,
                    reset: false,
                    unreadOnly,
                    limit: pageLimit,
                })
            )
        )
    }, [
        dispatch,
        canLoadMore,
        nextSkip,
        list.length,
        unreadOnly,
        pageLimit,
    ])

    // If the first page does not fill the panel, fetch more until scrollable or no pages left
    useEffect(() => {
        if (!canLoadMore || list.length === 0) return

        const el = document.getElementById(NOTIFICATIONS_SCROLL_ID)
        if (!el) return

        const needsMore =
            el.scrollHeight <= el.clientHeight + 8 && hasNext
        if (needsMore) {
            loadMoreNotifications()
        }
    }, [canLoadMore, hasNext, list.length, loadMoreNotifications])

    const handleMarkAllRead = async () => {
        try {
            await dispatch(markAllSupportNotificationsRead()).unwrap()
            showActionNotification({
                type: 'success',
                message: 'Marked all as read',
                notificationKey: 'notifications-mark-all-ok',
            })
        } catch {
            showActionNotification({
                type: 'error',
                message: 'Could not mark all as read',
                notificationKey: 'notifications-mark-all-fail',
            })
        }
    }

    const moreMenu = {
        items: [
            {
                key: 'mark-read',
                label: 'Mark all as read',
                onClick: () => handleMarkAllRead(),
            },
        ],
    }

    const handleRowClick = async (item) => {
        const notificationId = item?._id
        const unread = !item?.notificationData?.isReaded
        if (notificationId && unread) {
            try {
                await dispatch(
                    markSupportNotificationRead(notificationId)
                ).unwrap()
            } catch {
                /* continue to navigate */
            }
        }

        const type = item?.notificationType
        const routeId = item?.routeId?.[0]

        if (type === 'supportTasks') {
            const { projectId, taskId } = pickSupportTaskProjectAndTaskIds(item)
            if (projectId && taskId) {
                navigate(
                    PRIVATE_ROUTES.projects.projectIdViewTask(
                        projectId,
                        'board',
                        taskId
                    )
                )
            } else {
                navigate(PRIVATE_ROUTES.home)
            }
        } else if (routeId) {
            navigate(PRIVATE_ROUTES.projects.projectId(String(routeId)))
        } else {
            navigate(PRIVATE_ROUTES.home)
        }
        onClose?.()
    }

    const typeLabel = (n) => {
        const mapped = NOTIFICATION_TYPE_LABEL[n?.notificationType]
        if (mapped) return formatTypeLabel(mapped)
        return formatTypeLabel(n?.notificationType || 'notification')
    }

    return (
        <div className={styles.panel}>
            <div className={styles.panelHeader}>
                <h2 className={styles.panelTitle}>Notifications</h2>
                <div className={styles.panelHeaderRight}>
                    <label className={styles.unreadToggle}>
                        <span>Only show unread</span>
                        <Switch
                            size="small"
                            checked={unreadOnly}
                            onChange={handleUnreadOnlyChange}
                        />
                    </label>

                    <Dropdown menu={moreMenu} trigger={['click']}>
                        <button
                            type="button"
                            className={styles.iconBtn}
                            aria-label="More actions"
                        >
                            <Icon
                                icon="mdi:dots-vertical"
                                width={18}
                                height={18}
                            />
                        </button>
                    </Dropdown>
                </div>
            </div>

            <div className={styles.sectionLabel}>Recent</div>

            {error && status === 'error' && list.length === 0 ? (
                <div className={styles.error}>{String(error)}</div>
            ) : (
                <div id={NOTIFICATIONS_SCROLL_ID} className={styles.list}>
                    {isLoading && list.length === 0 ? (
                        <div className={styles.skeletonRow}>
                            {[1, 2, 3, 4].map((k) => (
                                <div key={k} style={{ marginBottom: 12 }}>
                                    <Skeleton
                                        active
                                        avatar
                                        paragraph={{ rows: 2 }}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : list.length === 0 ? (
                        <div className={styles.empty}>
                            {unreadOnly
                                ? 'No unread notifications.'
                                : 'No notifications yet.'}
                        </div>
                    ) : (
                        <InfiniteScroll
                            dataLength={list.length}
                            next={loadMoreNotifications}
                            hasMore={canLoadMore}
                            scrollableTarget={NOTIFICATIONS_SCROLL_ID}
                            scrollThreshold={0.85}
                            loader={
                                <div className={styles.listLoadingMore}>
                                    <Icon
                                        icon="line-md:loading-loop"
                                        width={20}
                                        height={20}
                                        style={{ color: '#6366f1' }}
                                    />
                                    <span>Loading more…</span>
                                </div>
                            }
                            endMessage={
                                list.length > 0 ? (
                                    <div className={styles.endMessage}>
                                        You&apos;re all caught up
                                    </div>
                                ) : null
                            }
                            style={{ overflow: 'visible' }}
                        >
                            {list.map((item) => {
                                const data = item?.notificationData || {}
                                const body = data?.notificationBody || ''
                                const actor = getActorDisplay(item)
                                const unread = !data?.isReaded
                                const quoted = pickQuotedTitle(body)
                                return (
                                    <div
                                        key={item?._id || data?._id}
                                        className={styles.row}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleRowClick(item)}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault()
                                                handleRowClick(item)
                                            }
                                        }}
                                    >
                                        <Avatar
                                            size={36}
                                            src={
                                                actor.profilePicture ||
                                                undefined
                                            }
                                            className={styles.rowAvatar}
                                            style={{
                                                backgroundColor:
                                                    getActorAvatarColor(
                                                        actor.name
                                                    ),
                                                flexShrink: 0,
                                                fontSize: 12,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {getInitials(actor.name)}
                                        </Avatar>
                                        <div className={styles.rowBody}>
                                            <div className={styles.rowTop}>
                                                <span>
                                                    {body || 'Notification'}
                                                </span>
                                                <span
                                                    className={styles.rowTime}
                                                >
                                                    {formatRelativeTime(
                                                        item?.notificationTime
                                                    )}
                                                </span>
                                            </div>
                                            {quoted ? (
                                                <div
                                                    className={
                                                        styles.rowTaskRef
                                                    }
                                                >
                                                    <Icon
                                                        icon="mdi:clipboard-text-outline"
                                                        width={14}
                                                        height={14}
                                                        className={
                                                            styles.rowTaskRefIcon
                                                        }
                                                    />
                                                    <span
                                                        className={
                                                            styles.rowTaskRefText
                                                        }
                                                        title={quoted}
                                                    >
                                                        {quoted}
                                                    </span>
                                                </div>
                                            ) : null}
                                            <div className={styles.rowMeta}>
                                                <span
                                                    className={`${styles.rowMetaBadge} ${getTypeBadgeClassName(item?.notificationType)}`}
                                                >
                                                    {typeLabel(item)}
                                                </span>
                                            </div>
                                        </div>
                                        {unread ? (
                                            <span
                                                className={styles.rowDot}
                                                aria-label="Unread"
                                            />
                                        ) : (
                                            <span
                                                style={{
                                                    width: 8,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                    </div>
                                )
                            })}
                        </InfiniteScroll>
                    )}
                    {canLoadMore && list.length > 0 && !isLoadingMore ? (
                        <div className={styles.footer}>
                            <button
                                type="button"
                                className={styles.loadMore}
                                onClick={loadMoreNotifications}
                            >
                                Load more notifications
                            </button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    )
}

const HeaderNotifications = () => {
    const dispatch = useDispatch()
    const { userProfile } = useSelector(loginState)
    const { unreadCount } = useSelector(supportNotificationsState)
    const [open, setOpen] = useState(false)
    const [unreadOnly, setUnreadOnly] = useState(false)

    const userId = userProfile?._id || userProfile?.id

    useEffect(() => {
        if (!userId) return
        dispatch(
            fetchSupportNotifications(
                buildNotificationFetchArgs({ skip: 0, reset: true })
            )
        )
    }, [dispatch, userId])

    const onOpenChange = (next) => {
        setOpen(next)
        if (next) {
            dispatch(
                fetchSupportNotifications(
                    buildNotificationFetchArgs({
                        skip: 0,
                        reset: true,
                        unreadOnly,
                    })
                )
            )
        }
    }

    if (!userProfile || !userId) return null

    return (
        <div className={styles.wrap}>
            <Popover
                open={open}
                onOpenChange={onOpenChange}
                trigger="click"
                placement="bottomRight"
                arrow={false}
                styles={{
                    root: {
                        maxWidth: 'none',
                    },
                    body: {
                        padding: 0,
                        borderRadius: '14px',
                        width: 'min(440px, calc(100vw - 28px))',
                        boxSizing: 'border-box',
                    },
                }}
                overlayStyle={{ paddingTop: 8 }}
                content={
                    <NotificationsPanel
                        unreadOnly={unreadOnly}
                        onUnreadOnlyChange={setUnreadOnly}
                        onClose={() => setOpen(false)}
                    />
                }
            >
                <button
                    type="button"
                    className={styles.bellBtn}
                    aria-label="Notifications"
                >
                    <Badge count={unreadCount} size="small" showZero={false}>
                        <Icon
                            icon="mdi:bell-outline"
                            width={22}
                            height={22}
                            className={styles.bellIcon}
                            style={{
                                display: 'block',
                                color: 'rgba(255, 255, 255, 0.95)',
                            }}
                        />
                    </Badge>
                </button>
            </Popover>
        </div>
    )
}

export default HeaderNotifications
